import React, { useState, useEffect } from 'react';
import './adminpageretrival.css';

const ExpenseCategoryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(''); // Holds the selected category
  const [expenses, setExpenses] = useState([]);
  const [error, setError] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState(''); // Holds the approval/disapproval message

  // Automatically fetch expenses based on selected category
  const fetchExpenses = async (category) => {
    setError(null);
    setExpenses([]);  // Clear previous expenses
    setApprovalMessage(''); // Clear the previous message when fetching new data

    if (!category) {
      setError('Please select a category.');
      return;
    }

    try {
      const response = await fetch(
        `https://cleezoclass.com:8443/api/expensesByCategory?category=${encodeURIComponent(category)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch expenses');
      }

      const data = await response.json();
      setExpenses(data.data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Automatically detect the category change and fetch expenses
  useEffect(() => {
    // Optionally, do not set any category from localStorage on refresh (commented this out)
    // const savedCategory = localStorage.getItem('selectedCategory');
    // if (savedCategory) {
    //   setSelectedCategory(savedCategory);
    // }

    // Automatically clear the stored category upon page load or refresh
    setSelectedCategory('');
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchExpenses(selectedCategory);
    }
  }, [selectedCategory]);

  // Handle approval or disapproval change
  const handleApprovalStatus = async (empId, status, category) => {
    console.log('Updating status:', { empId, status, category }); // Debug log

    try {
      const response = await fetch('https://cleezoclass.com:8443/api/updateApprovalStatus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          empId: empId, // Pass empId
          approvalStatus: status, // Pass status
          category: category, // Pass category
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setApprovalMessage(data.message); // Set the success message

        // Update the expense status in the local state after approval/disapproval
        setExpenses((prev) =>
          prev.map((expense) =>
            expense.empId === empId ? { ...expense, approvalStatus: status } : expense
          )
        );
      } else {
        setApprovalMessage(data.error || 'Failed to update status'); // Set the error message
      }
    } catch (error) {
      console.error('Error:', error);
      setApprovalMessage('An error occurred while updating the status');
    }
  };

  // Render tables for different categories
  const renderExpensesTable = (category) => {
    if (!expenses.length) return <p className="error-message">No expenses found for the selected category.</p>;

    const columns = category === 'Travel Expenses'
      ? ['Category', 'Emp ID', 'Start Date', 'End Date', 'Mode', 'Distance', 'City', 'Amount', 'Approval']
      : category === 'Accommodation Expenses'
      ? ['Category', 'Emp ID', 'Accommodation Type', 'Start Date', 'End Date', 'Amount', 'Duration', 'Receipt', 'Approval']
      : category === 'Marketing and Advertising'
      ? ['Category', 'Emp ID', 'Campaign Name', 'Start Date', 'End Date', 'Amount', 'Platform', 'Approval']
      : ['Category', 'Emp ID', 'Expense Name', 'Start Date', 'Amount', 'Payment Method', 'Approval'];

    const renderRows = () => {
      return expenses.map((exp, index) => (
        <tr key={index}>
          <td>{category}</td> {/* Display category name here */}
          <td>{exp.empId || 'N/A'}</td>
          {category === 'Travel Expenses' && (
            <>
              <td>{exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.travelExpenseMode || 'N/A'}</td>
              <td>{exp.distanceTravelled || 'N/A'}</td>
              <td>{exp.city || 'N/A'}</td>
              <td>{exp.amount || 'N/A'}</td>
            </>
          )}
          {category === 'Accommodation Expenses' && (
            <>
              <td>{exp.accommodationType || 'N/A'}</td>
              <td>{exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.amount || 'N/A'}</td>
              <td>{exp.accommodationDuration || 'N/A'}</td>
              <td>{exp.accommodationReceipt ? <a href={`data:application/octet-stream;base64,${exp.accommodationReceipt}`} download="receipt">Download Receipt</a> : 'No Receipt'}</td>
            </>
          )}
          {category === 'Marketing and Advertising' && (
            <>
              <td>{exp.campaignName || 'N/A'}</td>
              <td>{exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.amount || 'N/A'}</td>
              <td>{exp.platform || 'N/A'}</td>
            </>
          )}
          {category === 'Miscellaneous Expenses' && (
            <>
              <td>{exp.expenseName || 'N/A'}</td>
              <td>{exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'}</td>
              <td>{exp.amount || 'N/A'}</td>
              <td>{exp.paymentMethod || 'N/A'}</td>
            </>
          )}
          <td>
            <button
              onClick={() => handleApprovalStatus(exp.empId, 'Approved', category)} // Handle Approval
              className="btn approve"
            >
              Approve
            </button>
            <button
              onClick={() => handleApprovalStatus(exp.empId, 'Disapproved', category)} // Handle Disapproval
              className="btn disapprove"
            >
              Disapprove
            </button>
          </td>
        </tr>
      ));
    };

    return (
      <div className="table-container">
        <table className="expense-table">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="page-container">
      <h1 className="header">Expense Management</h1>
      
      {/* Select Category and Update Local Storage */}
      <div className="category-select-container">
        <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="category-select">
          <option value="">Select Category</option>
          <option value="Travel Expenses">Travel Expenses</option>
          <option value="Accommodation Expenses">Accommodation Expenses</option>
          <option value="Marketing and Advertising">Marketing and Advertising</option>
          <option value="Miscellaneous Expenses">Miscellaneous Expenses</option>
        </select>
      </div>

      {/* Display the Category Name */}
      {selectedCategory && <h2 className="category-title">{selectedCategory}</h2>}

      {error && <p className="error-message">{error}</p>}
      <div className="expenses-container">
        {selectedCategory && renderExpensesTable(selectedCategory)}
        {selectedCategory && !expenses.length && <p className="error-message">No expenses found for this category.</p>}
      </div>

      {approvalMessage && (
        <div className="approval-message">
          <p>{approvalMessage}</p>
        </div>
      )}
    </div>
  );
};

export default ExpenseCategoryPage;
